// ─── generators/maandrapport.js ──────────────────────────────────────────────────
// Rapportgenerator: genMaandrapport.
// Wijzig hier bij aanpassingen aan dit specifieke rapporttype.

import { MAANDEN, MAANDEN_K, fmtE, fmtD, fmtKg, fmtK, diff, statusLabel, statusBadge, isOnderweg } from '../helpers.js';
import { header, kpiBlok, pageCss, sectTitel, sectTitelKlein, footer, wrapMetHerhalendeHeader } from '../layout.js';
import { balkGrafiek, balkGrafiek3D, lijnGrafiek, taartGrafiek, dubbeleGrafiek, hBalk, renderRanking, renderGrafiek } from '../charts.js';
import { verwerkData } from '../data.js';

function genMaandrapport(aanvragen, bedrijfsnaam, cfg, logo) {
  const jaar  = cfg.jaar  || new Date().getFullYear();
  const maand = cfg.maand ?? new Date().getMonth();
  const MAANDEN_LANG = ['Januari','Februari','Maart','April','Mei','Juni','Juli',
                        'Augustus','September','Oktober','November','December'];
  const maandNaam = MAANDEN_LANG[maand];
  const gt  = cfg.grafiekType          || 'balk3d';
  const gtB = cfg.grafiekTypeBestemming || 'hbalk';
  const gtG = cfg.grafiekTypeGoed       || 'taart';

  // Vergelijkingsmodus: 'nee' | 'vorigeMaand' | 'vorigJaar'
  const modus = cfg.vergelijkModus ||
    (cfg.vergelijkVorigeMaand === false ? 'nee' : 'vorigeMaand');

  // Vorige maand
  const vMaand = maand === 0 ? 11 : maand - 1;
  const vJaarM = maand === 0 ? jaar - 1 : jaar;
  // Zelfde maand vorig jaar
  const vJaarJ = jaar - 1;

  // Vergelijkingsperiode bepalen
  const vMaandRef = modus === 'vorigJaar' ? maand  : vMaand;
  const vJaarRef  = modus === 'vorigJaar' ? vJaarJ : vJaarM;

  // Filter zendingen van geselecteerde maand
  const lijstMaand = aanvragen.filter(a => {
    const d = new Date(a.Datum || a.LaadDatum || 0);
    return d.getFullYear() === jaar && d.getMonth() === maand;
  });
  const lijstVorig = modus !== 'nee' ? aanvragen.filter(a => {
    const d = new Date(a.Datum || a.LaadDatum || 0);
    return d.getFullYear() === vJaarRef && d.getMonth() === vMaandRef;
  }) : [];

  // Bereken statistieken
  const totK  = lijstMaand.reduce((s,a)=>s+(parseFloat(a.Factuur)||parseFloat(a.KlantPrijs)||0),0);
  const totKV = lijstVorig.reduce((s,a)=>s+(parseFloat(a.Factuur)||parseFloat(a.KlantPrijs)||0),0);
  let totGew=0, totColli=0;
  lijstMaand.forEach(a=>{[1,2,3,4,5].forEach(n=>{
    totGew+=parseFloat(a[`Goed${n}_Gewicht`])||0;
    totColli+=parseInt(a[`Goed${n}_Aantal`])||0;
  });});
  const gemK = lijstMaand.length > 0 ? totK/lijstMaand.length : 0;

  // Bestemmingen en goederensoorten
  const landMap={}, goedMap={};
  lijstMaand.forEach(a=>{
    const l=a.LosLand||''; if(l) landMap[l]=(landMap[l]||0)+1;
    [1,2,3,4,5].forEach(n=>{
      const g=a[`Goed${n}_Omschrijving`]||a[`Goed${n}_Soort`]||'';
      if(g) goedMap[g]=(goedMap[g]||0)+1;
    });
  });
  const topLanden = Object.entries(landMap).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([l,v])=>({l,v}));
  const topGoed   = Object.entries(goedMap).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([l,v])=>({l,v}));

  // Kostenverdeling per week in deze maand (dag 1-7, 8-14, 15-21, 22-einde)
  const weken = ['Week 1','Week 2','Week 3','Week 4'];
  const kostenWeken = [0,0,0,0];
  const volWeken    = [0,0,0,0];
  lijstMaand.forEach(a=>{
    const dag = new Date(a.Datum||a.LaadDatum||0).getDate();
    const wi  = Math.min(Math.floor((dag-1)/7), 3);
    const b   = parseFloat(a.Factuur)||parseFloat(a.KlantPrijs)||0;
    kostenWeken[wi]+=b; volWeken[wi]++;
  });

  const _css = '<style>' + pageCss(cfg.orientatie) + '</style>';
  const _hdr = header(`Maandrapport — ${maandNaam} ${jaar}`,
    `${lijstMaand.length} zendingen${lijstVorig.length>0?` · Vergeleken met ${MAANDEN_LANG[vMaandRef]} ${vJaarRef}`:''}`,
    bedrijfsnaam, logo);
  let body = '';

  const heeftVorig = modus !== 'nee' && lijstVorig.length > 0;
  const gemKV = lijstVorig.length > 0 ? totKV/lijstVorig.length : 0;
  let totGewV=0;
  lijstVorig.forEach(a=>{[1,2,3,4,5].forEach(n=>{totGewV+=parseFloat(a[`Goed${n}_Gewicht`])||0;});});

  // ── KPI vergelijkingstabel ──
  const kpis = [
    {lbl:'Transportkosten', huid:fmtE(totK), vor:heeftVorig?fmtE(totKV):'—', delta:heeftVorig?diff(totK,totKV):'', k:'#3b82f6'},
    {lbl:'Zendingen',       huid:lijstMaand.length, vor:heeftVorig?lijstVorig.length:'—', delta:heeftVorig?diff(lijstMaand.length,lijstVorig.length):'', k:'#1a364c'},
    {lbl:'Gem. per zending',huid:gemK>0?fmtE(gemK):'—', vor:heeftVorig&&gemKV>0?fmtE(gemKV):'—', delta:heeftVorig&&gemKV>0?diff(gemK,gemKV):'', k:'#d4a848'},
    {lbl:'Totaal gewicht',  huid:totGew>0?fmtKg(totGew):'—', vor:heeftVorig&&totGewV>0?fmtKg(totGewV):'—', delta:'', k:'#8b5cf6'},
  ];

  if(heeftVorig) {
    // Vergelijkingstabel: huidige vs vorige maand
    body += `<div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin-bottom:10px;">
      <table style="width:100%;border-collapse:collapse;font-size:9px;">
        <thead>
          <tr style="background:#1a364c;">
            <th style="padding:7px 10px;text-align:left;color:rgba(255,255,255,.6);font-size:7.5px;text-transform:uppercase;letter-spacing:.06em;font-weight:600;">Indicator</th>
            <th style="padding:7px 10px;text-align:right;color:white;font-size:9px;font-weight:700;">${maandNaam} ${jaar}</th>
            <th style="padding:7px 10px;text-align:right;color:rgba(255,255,255,.7);font-size:9px;font-weight:600;">${MAANDEN_LANG[vMaandRef]} ${vJaarRef}</th>
            <th style="padding:7px 10px;text-align:center;color:rgba(255,255,255,.6);font-size:7.5px;text-transform:uppercase;letter-spacing:.06em;font-weight:600;">Δ Verschil</th>
          </tr>
        </thead>
        <tbody>
          ${kpis.map((k,i)=>`
            <tr style="border-bottom:1px solid #f1f5f9;background:${i%2===0?'white':'#fafafa'};">
              <td style="padding:7px 10px;font-weight:600;color:#1a364c;">
                <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${k.k};margin-right:6px;vertical-align:middle;"></span>
                ${k.lbl}
              </td>
              <td style="padding:7px 10px;text-align:right;font-weight:700;color:#1a364c;font-variant-numeric:tabular-nums;">${k.huid}</td>
              <td style="padding:7px 10px;text-align:right;color:#64748b;font-variant-numeric:tabular-nums;">${k.vor}</td>
              <td style="padding:7px 10px;text-align:center;">${k.delta}</td>
            </tr>`).join('')}
        </tbody>
      </table>
    </div>`;
  } else {
    // Geen vergelijking: 4 KPI kaarten
    body += `<div style="display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:10px;">
      ${kpis.map(({lbl,huid,k})=>`
        <div style="border:1px solid #e2e8f0;border-top:3px solid ${k};border-radius:8px;padding:8px 10px;">
          <div style="font-size:7.5px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.05em;margin-bottom:4px;">${lbl}</div>
          <div style="font-size:15px;font-weight:800;color:#1a364c;line-height:1.1;font-variant-numeric:tabular-nums;">${huid}</div>
        </div>`).join('')}
    </div>`;
  }

  if(lijstMaand.length === 0) {
    body += `<div style="text-align:center;padding:40px;color:#94a3b8;font-size:12px;border:1px solid #e2e8f0;border-radius:8px;">
      Geen zendingen gevonden voor ${maandNaam} ${jaar}
    </div>`;
    body += footer();
    return wrapMetHerhalendeHeader(_css, _hdr, body);
  }

  // Kosten vorige maand per week (voor vergelijking)
  const kostenWekenV = [0,0,0,0];
  lijstVorig.forEach(a=>{
    const dag = new Date(a.Datum||a.LaadDatum||0).getDate();
    const wi  = Math.min(Math.floor((dag-1)/7), 3);
    kostenWekenV[wi]+=(parseFloat(a.Factuur)||parseFloat(a.KlantPrijs)||0);
  });

  // ── Grafieken rij: kosten per week + bestemmingen ──
  const heeftBestemming = cfg.inclBestemming !== false && topLanden.length > 0;
  body += `<div style="display:grid;grid-template-columns:${heeftBestemming?'1.5fr 1fr':'1fr'};gap:10px;margin-bottom:10px;">`;

  // Kosten per week — beide maanden als gestapelde balken
  body += `<div style="border:1px solid #e2e8f0;border-top:3px solid #3b82f6;border-radius:8px;padding:10px 12px;">
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
      <div style="font-size:8px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.06em;">Kosten per week</div>
      ${heeftVorig?`<div style="display:flex;gap:10px;font-size:8px;">
        <span><span style="display:inline-block;width:8px;height:8px;background:#1a364c;border-radius:1px;margin-right:3px;"></span>${maandNaam}</span>
        <span><span style="display:inline-block;width:8px;height:8px;background:#c2e6ef;border-radius:1px;margin-right:3px;"></span>${MAANDEN_LANG[vMaandRef]} ${vJaarRef}</span>
      </div>`:''}
    </div>
    ${heeftVorig ? dubbeleGrafiek(gt, kostenWeken, kostenWekenV, weken, '#1a364c', '#c2e6ef', 110) : renderGrafiek(gt, kostenWeken, weken, '#1a364c', 110)}
  </div>`;

  // Bestemmingen
  if(heeftBestemming) {
    body += `<div class="blok" style="border:1px solid #e2e8f0;border-top:3px solid #d4a848;border-radius:8px;padding:10px 12px;page-break-inside:avoid;break-inside:avoid;">
      <div style="font-size:8px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;">Top bestemmingen</div>
      ${renderRanking(topLanden,n=>n+'x',gtB,120)}
    </div>`;
  }
  body += `</div>`;

  // ── Goederensoorten + zendingentabel ──
  const heeftGoed = cfg.inclGoed !== false && topGoed.length > 0;
  const heeftTabel = cfg.inclMaand !== false && lijstMaand.length > 0;

  if(heeftGoed || heeftTabel) {
    body += `<div style="display:grid;grid-template-columns:${heeftGoed&&heeftTabel?'1fr 1.5fr':heeftGoed?'1fr':'1fr'};gap:10px;margin-bottom:10px;">`;

    if(heeftGoed) {
      body += `<div style="border:1px solid #e2e8f0;border-top:3px solid #8b5cf6;border-radius:8px;padding:10px 12px;">
        <div style="font-size:8px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;">Goederensoorten</div>
        ${renderRanking(topGoed,n=>n+'x',gtG,110)}
      </div>`;
    }

    if(heeftTabel) {
      const tabelRijen = lijstMaand.slice(0,8).map((a,i)=>{
        const b=parseFloat(a.Factuur)||parseFloat(a.KlantPrijs)||0;
        let g=0;[1,2,3,4,5].forEach(n=>g+=parseFloat(a[`Goed${n}_Gewicht`])||0);
        return `<tr style="border-bottom:1px solid #f5f5f5;background:${i%2===0?'white':'#fafafa'};">
          <td style="padding:4px 6px;font-weight:700;color:#1a364c;white-space:nowrap;">${a.AanvraagID}</td>
          <td style="padding:4px 6px;white-space:nowrap;">${a.LaadPlaats||a.LaadLand||'—'} → ${a.LosPlaats||a.LosLand||'—'}</td>
          <td style="padding:4px 6px;text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums;">${g>0?fmtKg(g):'—'}</td>
          <td style="padding:4px 6px;text-align:right;font-weight:600;white-space:nowrap;font-variant-numeric:tabular-nums;">${b>0?fmtE(b):'—'}</td>
        </tr>`;
      }).join('');
      body += `<div class="blok" style="border:1px solid #e2e8f0;border-top:3px solid #10b981;border-radius:8px;padding:10px 12px;page-break-inside:avoid;break-inside:avoid;">
        <div style="font-size:8px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;">
          Zendingen ${maandNaam}${lijstMaand.length>8?` (top 8 van ${lijstMaand.length})`:''}
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:8.5px;">
          <thead><tr style="background:#f7f6f2;">
            <th style="padding:4px 6px;text-align:left;font-size:7.5px;font-weight:700;color:#1a364c;text-transform:uppercase;border-bottom:1px solid #e8edf3;white-space:nowrap;">Nr.</th>
            <th style="padding:4px 6px;text-align:left;font-size:7.5px;font-weight:700;color:#1a364c;text-transform:uppercase;border-bottom:1px solid #e8edf3;">Route</th>
            <th style="padding:4px 6px;text-align:right;font-size:7.5px;font-weight:700;color:#1a364c;text-transform:uppercase;border-bottom:1px solid #e8edf3;white-space:nowrap;">Gewicht</th>
            <th style="padding:4px 6px;text-align:right;font-size:7.5px;font-weight:700;color:#1a364c;text-transform:uppercase;border-bottom:1px solid #e8edf3;white-space:nowrap;">Prijs excl.</th>
          </tr></thead>
          <tbody>${tabelRijen}</tbody>
          <tfoot><tr style="border-top:2px solid #1a364c;background:#f7f6f2;">
            <td colspan="2" style="padding:5px 6px;font-weight:700;font-size:8.5px;">Totaal</td>
            <td style="padding:5px 6px;text-align:right;font-weight:700;font-size:8.5px;font-variant-numeric:tabular-nums;">${totGew>0?fmtKg(totGew):'—'}</td>
            <td style="padding:5px 6px;text-align:right;font-weight:700;color:#1a364c;font-size:8.5px;font-variant-numeric:tabular-nums;">${fmtE(totK)}</td>
          </tr></tfoot>
        </table>
      </div>`;
    }
    body += `</div>`;
  }



  return wrapMetHerhalendeHeader(_css, _hdr, body);
}

export { genMaandrapport };
